import React, { useState } from 'react';
import PipelineView from './PipelineView';
import { useAppContext, useAppActions } from '../../context/AppContext';
import { 
  UserPlus, Search, Filter, Calendar, Phone, Mail, MapPin, 
  CheckCircle, Clock, AlertTriangle, Star, MessageSquare, 
  FileText, Download, Eye, Edit3, Plus, Trash2, Bell,
  Brain, Cpu, Sparkles, BarChart3, Award, Zap, Globe, Target,
  TrendingUp, Users, Shield, Activity
} from 'lucide-react';

const Enquiries = () => {
  const { state } = useAppContext();
  const actions = useAppActions();
  const { currentUser, deviceInfo } = state;
  const { optimization } = deviceInfo;

  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  // AI Enquiries Optimization Data
  const aiEnquiriesData = {
    optimization: {
      responseTime: 2.1,
      conversionRate: 78.5,
      satisfaction: 4.6,
      efficiency: 89.2
    },
    predictions: {
      highPriority: 3,
      conversionRisk: 2,
      responseDelays: 1,
      satisfactionDecline: 1
    },
    recommendations: [
      {
        id: 'ENQ_AI001',
        type: 'Priority',
        priority: 'High',
        title: 'Prioritize Dorothy Brown enquiry',
        description: 'AI detected critical urgency indicators requiring immediate attention.',
        impact: 'Improve response time by 60%',
        enquiries: ['ENQ003'],
        status: 'Pending',
        aiConfidence: 96
      },
      {
        id: 'ENQ_AI002',
        type: 'Conversion',
        priority: 'Medium',
        title: 'Follow up with Margaret Thompson family',
        description: 'AI analysis shows high conversion potential with proactive follow-up.',
        impact: 'Increase conversion by 25%',
        enquiries: ['ENQ001'],
        status: 'Recommended',
        aiConfidence: 87
      },
      {
        id: 'ENQ_AI003',
        type: 'Efficiency',
        priority: 'Low',
        title: 'Optimize enquiry assignment algorithm',
        description: 'AI suggests better matching of enquiries to staff expertise.',
        impact: 'Improve processing efficiency by 15%',
        enquiries: ['All Enquiries'],
        status: 'Suggested',
        aiConfidence: 79
      }
    ],
    analytics: {
      priorityDistribution: {
        critical: 1,
        high: 2,
        medium: 2,
        low: 0
      },
      responseTimes: {
        excellent: 3,
        good: 1,
        needsImprovement: 1
      },
      conversionTrends: {
        improving: 4,
        stable: 1,
        declining: 0
      }
    }
  };

  // Mock enquiries data
  const enquiries = [
    {
      id: 'ENQ001',
      type: 'Residential Care',
      status: 'New',
      priority: 'High',
      date: '2025-07-24',
      time: '09:30',
      name: 'Margaret Thompson',
      phone: '0412 345 678',
      email: 'margaret.thompson@email.com',
      age: 85,
      familyContact: 'Sarah Thompson (Daughter)',
      familyPhone: '0413 456 789',
      preferredFacility: 'Sunshine Gardens',
      careLevel: 'High',
      primaryConditions: ['Dementia', 'Diabetes', 'Mobility Issues'],
      urgency: 'Immediate placement needed',
      notes: 'Family seeking urgent placement due to recent fall and cognitive decline',
      source: 'GP Referral',
      assignedTo: 'Sarah Mitchell',
      lastContact: '2025-07-24 10:15',
      nextFollowUp: '2025-07-25 14:00',
      documents: ['Medical Assessment', 'GP Referral', 'Family Statement']
    },
    {
      id: 'ENQ002',
      type: 'Respite Care',
      status: 'In Progress',
      priority: 'Medium',
      date: '2025-07-23',
      time: '14:20',
      name: 'Robert Wilson',
      phone: '0414 567 890',
      email: 'robert.wilson@email.com',
      age: 78,
      familyContact: 'Jennifer Wilson (Wife)',
      familyPhone: '0415 678 901',
      preferredFacility: 'Bayview Aged Care',
      careLevel: 'Medium',
      primaryConditions: ['Stroke Recovery', 'Hypertension'],
      urgency: 'Planned respite in 2 weeks',
      notes: 'Family needs 2-week respite while daughter travels',
      source: 'Family Self-Referral',
      assignedTo: 'Maria Garcia',
      lastContact: '2025-07-23 15:30',
      nextFollowUp: '2025-07-26 11:00',
      documents: ['Respite Application', 'Medical Certificate']
    },
    {
      id: 'ENQ003',
      type: 'Palliative Care',
      status: 'Urgent',
      priority: 'Critical',
      date: '2025-07-24',
      time: '08:15',
      name: 'Dorothy Brown',
      phone: '0416 789 012',
      email: 'dorothy.brown@email.com',
      age: 92,
      familyContact: 'Michael Brown (Son)',
      familyPhone: '0417 890 123',
      preferredFacility: 'Sunshine Gardens',
      careLevel: 'Critical',
      primaryConditions: ['Advanced Cancer', 'Pain Management'],
      urgency: 'Immediate palliative care needed',
      notes: 'Patient discharged from hospital, requires immediate palliative care placement',
      source: 'Hospital Discharge',
      assignedTo: 'Dr. Robert Smith',
      lastContact: '2025-07-24 09:00',
      nextFollowUp: '2025-07-24 16:00',
      documents: ['Hospital Discharge Summary', 'Palliative Care Assessment']
    },
    {
      id: 'ENQ004',
      type: 'Dementia Care',
      status: 'Assessment',
      priority: 'High',
      date: '2025-07-22',
      time: '11:45',
      name: 'Harold Davis',
      phone: '0418 901 234',
      email: 'harold.davis@email.com',
      age: 87,
      familyContact: 'Lisa Davis (Daughter)',
      familyPhone: '0419 012 345',
      preferredFacility: 'Greenhill Aged Care',
      careLevel: 'High',
      primaryConditions: ['Alzheimer\'s Disease', 'Wandering', 'Aggression'],
      urgency: 'Safety concerns at home',
      notes: 'Family struggling with behavioral issues and safety concerns',
      source: 'Aged Care Assessment Team',
      assignedTo: 'Dr. Emily Wilson',
      lastContact: '2025-07-23 13:20',
      nextFollowUp: '2025-07-25 10:00',
      documents: ['ACAT Assessment', 'Behavioral Assessment', 'Safety Report']
    },
    {
      id: 'ENQ005',
      type: 'Residential Care',
      status: 'Waitlisted',
      priority: 'Medium',
      date: '2025-07-20',
      time: '16:30',
      name: 'Elizabeth Johnson',
      phone: '0420 123 456',
      email: 'elizabeth.johnson@email.com',
      age: 79,
      familyContact: 'David Johnson (Son)',
      familyPhone: '0421 234 567',
      preferredFacility: 'Rosewood Aged Care',
      careLevel: 'Medium',
      primaryConditions: ['Osteoarthritis', 'Mild Dementia'],
      urgency: 'Planned placement in 3 months',
      notes: 'Family planning ahead for future care needs',
      source: 'Family Self-Referral',
      assignedTo: 'Lisa Johnson',
      lastContact: '2025-07-22 14:15',
      nextFollowUp: '2025-07-29 15:00',
      documents: ['Application Form', 'Medical Assessment']
    }
  ];

  const tabs = [
    { id: 'all', label: 'All Enquiries', count: enquiries.length },
    { id: 'pipeline', label: 'Pipeline', count: 0, icon: BarChart3 },
    { id: 'new', label: 'New', count: enquiries.filter(e => e.status === 'New').length },
    { id: 'urgent', label: 'Urgent', count: enquiries.filter(e => e.priority === 'Critical').length },
    { id: 'assessment', label: 'Assessment', count: enquiries.filter(e => e.status === 'Assessment').length },
    { id: 'waitlisted', label: 'Waitlisted', count: enquiries.filter(e => e.status === 'Waitlisted').length },
    { id: 'ai', label: 'AI Insights', count: 0, icon: Brain }
  ];

  const filteredEnquiries = enquiries.filter(enquiry => {
    const matchesTab = activeTab === 'all' || 
                      (activeTab === 'new' && enquiry.status === 'New') ||
                      (activeTab === 'urgent' && enquiry.priority === 'Critical') ||
                      (activeTab === 'assessment' && enquiry.status === 'Assessment') ||
                      (activeTab === 'waitlisted' && enquiry.status === 'Waitlisted');
    
    const matchesSearch = enquiry.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         enquiry.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         enquiry.familyContact.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || enquiry.status === filterStatus;
    
    return matchesTab && matchesSearch && matchesStatus;
  });

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Critical': return 'bg-red-100 text-red-800';
      case 'High': return 'bg-orange-100 text-orange-800';
      case 'Medium': return 'bg-yellow-100 text-yellow-800';
      case 'Low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'New': return 'bg-blue-100 text-blue-800';
      case 'In Progress': return 'bg-yellow-100 text-yellow-800';
      case 'Urgent': return 'bg-red-100 text-red-800';
      case 'Assessment': return 'bg-purple-100 text-purple-800';
      case 'Waitlisted': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const renderEnquiryCard = (enquiry) => (
    <div key={enquiry.id} className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 hover:shadow-xl transition-all duration-300 min-h-[280px] flex flex-col">
      <div className="flex justify-between items-start mb-4 gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <h3 className="font-semibold text-lg text-gray-900 break-words">{enquiry.name}</h3>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className={`px-2.5 py-1 text-xs font-semibold rounded-full whitespace-nowrap ${getPriorityColor(enquiry.priority)}`}>
                {enquiry.priority}
              </span>
              <span className={`px-2.5 py-1 text-xs font-semibold rounded-full whitespace-nowrap ${getStatusColor(enquiry.status)}`}>
                {enquiry.status}
              </span>
            </div>
          </div>
          <div className="space-y-1">
            <p className="text-gray-600 text-sm">{enquiry.type} • Age {enquiry.age}</p>
            <p className="text-xs text-gray-500">{enquiry.date} at {enquiry.time}</p>
          </div>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <button 
            onClick={() => {
              console.log('View enquiry details');
            }}
            className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" 
            title="View Details"
          >
            <Eye className="w-4 h-4" />
          </button>
          <button 
            onClick={() => {
              console.log('Contact enquiry');
            }}
            className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors" 
            title="Contact"
          >
            <Phone className="w-4 h-4" />
          </button>
          <button 
            onClick={() => {
              console.log('Edit enquiry');
            }}
            className="p-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors" 
            title="Edit"
          >
            <Edit3 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-700 mb-1">Contact Information</p>
          <p className="text-sm text-gray-600 break-words">{enquiry.phone}</p>
          <p className="text-sm text-gray-600 break-words truncate">{enquiry.email}</p>
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-700 mb-1">Family Contact</p>
          <p className="text-sm text-gray-600 break-words">{enquiry.familyContact}</p>
          <p className="text-sm text-gray-600 break-words">{enquiry.familyPhone}</p>
        </div>
      </div>

      <div className="mb-4">
        <p className="text-sm font-medium text-gray-700 mb-2">Medical Conditions</p>
        <div className="flex flex-wrap gap-2">
          {enquiry.primaryConditions.map((condition, index) => (
            <span key={index} className="px-2.5 py-1 text-xs rounded-full bg-gray-100 text-gray-700 whitespace-nowrap">
              {condition}
            </span>
          ))}
        </div>
      </div>

      <div className="mb-4 flex-1">
        <p className="text-sm font-medium text-gray-700 mb-1">Notes</p>
        <p className="text-sm text-gray-600 line-clamp-2 break-words">{enquiry.notes}</p>
      </div>

      <div className="flex flex-wrap justify-between items-center gap-3 mt-auto pt-2 border-t border-gray-100">
        <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500">
          <span className="flex items-center gap-1 whitespace-nowrap">
            <UserPlus className="w-4 h-4 flex-shrink-0" />
            <span className="truncate max-w-[120px]">{enquiry.assignedTo}</span>
          </span>
          <span className="flex items-center gap-1 whitespace-nowrap">
            <Clock className="w-4 h-4 flex-shrink-0" />
            <span className="text-xs">{enquiry.lastContact}</span>
          </span>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <button 
            onClick={() => {
              console.log('Follow up enquiry');
            }}
            className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
          >
            Follow Up
          </button>
          <button 
            onClick={() => {
              console.log('Schedule assessment');
            }}
            className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded-lg hover:bg-green-200"
          >
            Schedule Assessment
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-6 text-white shadow-xl min-h-[120px] flex items-center">
        <div className="flex justify-between items-center w-full">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              Enquiries Management
              <UserPlus className="w-8 h-8 text-blue-300" />
            </h1>
            <p className="text-base mt-2 opacity-90">
              Manage aged care enquiries, applications, and family communications
            </p>
          </div>
          <div className="flex gap-2">
            <button 
              onClick={() => {
                console.log('Create new enquiry');
              }}
              className={`bg-white text-blue-600 ${optimization.buttonSize} rounded-xl font-semibold hover:bg-opacity-90 ${optimization.animationSpeed} flex items-center gap-2`}
            >
              <Plus className={`${optimization.iconSize}`} />
              New Enquiry
            </button>
            <button 
              onClick={() => {
                console.log('Export enquiries');
              }}
              className={`bg-white text-blue-600 ${optimization.buttonSize} rounded-xl font-semibold hover:bg-opacity-90 ${optimization.animationSpeed} flex items-center gap-2`}
            >
              <Download className={`${optimization.iconSize}`} />
              Export
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <UserPlus className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">{enquiries.length}</h3>
              <p className="text-sm text-gray-600">Total Enquiries</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">{enquiries.filter(e => e.priority === 'Critical').length}</h3>
              <p className="text-sm text-gray-600">Urgent Cases</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Clock className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">{enquiries.filter(e => e.status === 'Assessment').length}</h3>
              <p className="text-sm text-gray-600">Pending Assessment</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">2.1 days</h3>
              <p className="text-sm text-gray-600">Avg Response Time</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search enquiries by name, type, or family contact..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="New">New</option>
              <option value="In Progress">In Progress</option>
              <option value="Urgent">Urgent</option>
              <option value="Assessment">Assessment</option>
              <option value="Waitlisted">Waitlisted</option>
            </select>
            <button 
              onClick={() => {
                console.log('Apply filters');
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <Filter className="w-4 h-4" />
              Filter
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-6">
          <div className="flex space-x-1 overflow-x-auto">
            {tabs.map(tab => {
              const isActive = activeTab === tab.id;
              const IconComponent = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-all whitespace-nowrap ${
                    isActive ? 'bg-blue-100 text-blue-600' : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {IconComponent && <IconComponent className="w-4 h-4" />}
                  <span className={optimization.fontSize}>{tab.label}</span>
                  {tab.count > 0 && (
                    <span className="px-2 py-1 text-xs rounded-full bg-gray-200 text-gray-700">
                      {tab.count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        {activeTab === 'pipeline' ? (
          <PipelineView />
        ) : activeTab === 'ai' ? (
          <div className="space-y-6">
            {/* AI Enquiries Optimization Dashboard */}
            <div className="bg-gradient-to-r from-purple-50 to-indigo-100 p-8 rounded-2xl border border-purple-200">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-purple-800 mb-2 flex items-center gap-3">
                    <Brain className="w-6 h-6 text-purple-600" />
                    AI-Powered Enquiry Optimization
                  </h3>
                  <p className="text-purple-600">Intelligent enquiry management and response optimization</p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-sm text-green-600 font-medium">AI Active</span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="bg-white p-6 rounded-xl shadow-sm border border-purple-200">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-3 bg-gradient-to-br from-green-500 to-green-600 rounded-xl">
                      <Clock className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-bold text-green-600">{aiEnquiriesData.optimization.responseTime}h</div>
                      <div className="text-sm text-gray-600">Response Time</div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Target</span>
                      <span className="text-green-600">2h</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-gradient-to-r from-green-500 to-green-600 h-2 rounded-full" style={{width: '95%'}}></div>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-sm border border-purple-200">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl">
                      <TrendingUp className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-bold text-blue-600">{aiEnquiriesData.optimization.conversionRate}%</div>
                      <div className="text-sm text-gray-600">Conversion Rate</div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Target</span>
                      <span className="text-blue-600">75%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full" style={{width: `${aiEnquiriesData.optimization.conversionRate}%`}}></div>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-sm border border-purple-200">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-3 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl">
                      <Star className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-bold text-purple-600">{aiEnquiriesData.optimization.satisfaction}/5</div>
                      <div className="text-sm text-gray-600">Satisfaction</div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Rating</span>
                      <span className="text-purple-600">Excellent</span>
                    </div>
                    <div className="text-xs text-gray-500">Family feedback</div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-sm border border-purple-200">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-3 bg-gradient-to-br from-green-500 to-green-600 rounded-xl">
                      <Zap className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-bold text-green-600">{aiEnquiriesData.optimization.efficiency}%</div>
                      <div className="text-sm text-gray-600">Efficiency</div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Process</span>
                      <span className="text-green-600">Optimized</span>
                    </div>
                    <div className="text-xs text-gray-500">AI-driven</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredEnquiries.length > 0 ? (
              filteredEnquiries.map(renderEnquiryCard)
            ) : (
              <div className="text-center py-8">
                <UserPlus className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-600 mb-2">No enquiries found</h3>
                <p className="text-gray-500">Try adjusting your search or filter criteria</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Enquiries;
